/**
 * Documentos — Pantalla global de búsqueda/revisión de documentos.
 *
 * Lista todos los docs subidos via DocumentAttacher (cualquier ref):
 * OT, OC, Cotización, Compra, Pieza catálogo, PieceInstance.
 *
 * Filtros: docType, búsqueda en nombre/label, fecha. Cards top con conteos
 * por tipo. Acción: abrir/descargar.
 *
 * Backend: GET /document?docType=X&includeDeleted=true&onlyDeleted=false
 * (ya soporta filtros — extendido en route).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import backend from "../../backend";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";

const DOC_TYPE_LABELS = {
  facturaEmitida:      "Factura emitida",
  facturaRecibida:     "Factura recibida",
  guiaDespacho:        "Guía de despacho",
  notaCredito:         "Nota crédito",
  notaDebito:          "Nota débito",
  boleta:              "Boleta",
  ocCliente:           "OC del cliente",
  planoTecnico:        "Plano técnico",
  controlDimensional:  "Control dimensional",
  dosierCalidad:       "Dossier de calidad",
  certificadoMaterial: "Certificado material",
  fotoTrabajo:         "Foto de trabajo",
  xml:                 "XML SII",
  otro:                "Otro",
};

const DOC_TYPE_COLORS = {
  facturaEmitida:      "#2ecc71",
  facturaRecibida:     "#16a085",
  guiaDespacho:        "#3498db",
  notaCredito:         "#e67e22",
  notaDebito:          "#e67e22",
  boleta:              "#1abc9c",
  ocCliente:           "#a78bfa",
  planoTecnico:        "#3498db",
  controlDimensional:  "#f39c12",
  dosierCalidad:       "#9b59b6",
  certificadoMaterial: "#e67e22",
  fotoTrabajo:         "#888",
  xml:                 "#666",
  otro:                "#888",
};

const fmtDate = iso => iso ? new Date(iso).toLocaleDateString("es-CL", {
  day: "2-digit", month: "short", year: "2-digit",
}) : "—";

const fmtSize = bytes => {
  if (!bytes) return "—";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const TRIBUTARY_TYPES = ["facturaEmitida", "facturaRecibida", "guiaDespacho", "notaCredito", "notaDebito", "boleta"];

const Documentos = () => {
  useTitle("Documentos");
  // Rama móvil (26-ago): lista de tarjetas en vez de tabla ancha.
  const isMobile = useIsMobile();

  const [docs,        setDocs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState("");
  const [filterType,  setFilterType]  = useState("all");
  const [showTrash,   setShowTrash]   = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [mutating,    setMutating]    = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const url = showTrash ? "document?onlyDeleted=true" : "document?includeDeleted=false";
    backend.get(url)
      .then(({ data }) => setDocs(Array.isArray(data) ? data : []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, [showTrash]);

  const subir = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const docType = window.prompt("Tipo de documento", "otro") || "otro";
    const label = window.prompt("Etiqueta (opcional)", "") || "";
    const body = new FormData();
    body.append("file", file);
    body.append("docType", docType);
    if (label) body.append("label", label);
    setUploading(true);
    try {
      const { data } = await backend.post("document/upload", body, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs(prev => [data, ...prev]);
    } catch (err) {
      alert(err?.response?.data?.error || "No se pudo subir el documento");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const eliminar = async d => {
    const tributario = TRIBUTARY_TYPES.includes(d.docType);
    let reason = null;
    if (!showTrash && tributario) {
      reason = window.prompt("Motivo de eliminación (mínimo 10 caracteres)", "");
      if (reason === null) return;
      if (reason.trim().length < 10) return alert("El motivo debe tener al menos 10 caracteres");
    } else if (!window.confirm(`¿Eliminar definitivamente “${d.originalName}”?`)) return;
    setMutating(d._id);
    try {
      await backend.delete(`document/${d._id}`, { data: { reason, force: showTrash } });
      setDocs(prev => prev.filter(row => row._id !== d._id));
    } catch (err) {
      alert(err?.response?.data?.error || "No se pudo eliminar el documento");
    } finally {
      setMutating(null);
    }
  };

  const filtered = useMemo(() => {
    let rows = docs;
    if (filterType !== "all") rows = rows.filter(d => d.docType === filterType);
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      rows = rows.filter(d =>
        (d.originalName || "").toLowerCase().includes(q) ||
        (d.label        || "").toLowerCase().includes(q) ||
        (d.docType      || "").toLowerCase().includes(q)
      );
    }
    return rows;
  }, [docs, search, filterType]);

  const countsByType = useMemo(() => {
    const c = { all: docs.length };
    for (const d of docs) c[d.docType] = (c[d.docType] || 0) + 1;
    return c;
  }, [docs]);

  // Auditoría 28-ago: el <a href="/api/document/:id/download"> iba SIN el
  // header Authorization → 401 siempre. Ahora la descarga va por axios
  // (backend) con responseType blob — mismo patrón que Liquidaciones.verPdf.
  const [descargando, setDescargando] = useState(null);
  const descargar = async d => {
    setDescargando(d._id);
    try {
      const res = await backend.get(`document/${d._id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = d.originalName || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert(`No se pudo descargar: ${e?.response?.data?.error || e.message}`);
    }
    setDescargando(null);
  };

  const refOf = d => {
    if (d.workOrderRefs?.length)     return { kind: "OT",  count: d.workOrderRefs.length };
    if (d.customerOrderRefs?.length) return { kind: "OC",  count: d.customerOrderRefs.length };
    if (d.quotationRefs?.length)     return { kind: "COT", count: d.quotationRefs.length };
    if (d.purchaseOrderRefs?.length) return { kind: "OC compra", count: d.purchaseOrderRefs.length };
    if (d.pieceInstanceRef)          return { kind: "Fab.", count: 1 };
    if (d.pieceRef)                  return { kind: "Pieza", count: 1 };
    return { kind: "—", count: 0 };
  };

  return (
    <div style={{
      display: "block", padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
      minHeight: "100%", color: "#ddd", fontFamily: "Arial,sans-serif",
      fontSize: 13, textAlign: "left", boxSizing: "border-box", width: "100%",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>
          Documentos
          <span style={{ marginLeft: 10, fontSize: 11, color: "#cbd5e1", fontWeight: 400 }}>
            búsqueda y revisión global
          </span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {!showTrash && <>
            <input ref={fileRef} type="file" onChange={subir} style={{ display: "none" }} />
            <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()}
              style={{ background: "#173b2a", border: "1px solid #2ecc7155", color: "#2ecc71", padding: "5px 12px", borderRadius: 5, fontSize: 11, cursor: uploading ? "wait" : "pointer" }}>
              {uploading ? "Subiendo…" : "＋ Subir documento"}
            </button>
          </>}
          <button
          onClick={() => setShowTrash(s => !s)}
          style={{
            background: showTrash ? "#3e2a2a" : "transparent",
            border: `1px solid ${showTrash ? "#e74c3c55" : "#444"}`,
            color: showTrash ? "#e74c3c" : "#aaa",
            padding: "5px 12px", borderRadius: 5, fontSize: 11, cursor: "pointer",
          }}
        >
          {showTrash ? "← Volver a activos" : "🗑 Ver papelera"}
          </button>
        </div>
      </div>

      {/* Cards de tipos clickables — chips-scroll: carrusel horizontal en móvil */}
      <div className="chips-scroll" style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        <DocCard label="Total" value={countsByType.all || 0} color="#3498db"
          onClick={() => setFilterType("all")} active={filterType === "all"} />
        {Object.keys(DOC_TYPE_LABELS).map(t => {
          const n = countsByType[t] || 0;
          if (n === 0) return null;
          return (
            <DocCard key={t}
              label={DOC_TYPE_LABELS[t]} value={n}
              color={DOC_TYPE_COLORS[t] || "#888"}
              onClick={() => setFilterType(p => p === t ? "all" : t)}
              active={filterType === t}
            />
          );
        })}
      </div>

      <input
        type="search"
        value={search}
        onChange={e => setSearch(e.target.value)}
        placeholder="Buscar por nombre, etiqueta, tipo…"
        style={{
          width: "100%", marginBottom: 12,
          background: "#161616", border: "1px solid #333", borderRadius: 5,
          padding: "8px 12px", color: "#ddd", fontSize: 12,
          fontFamily: "Arial,sans-serif", boxSizing: "border-box",
        }}
      />

      {/* Listado — móvil: tarjetas con botones táctiles; desktop: tabla */}
      {isMobile ? (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {loading && (
          <div style={{ padding: 20, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>Cargando…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: 20, textAlign: "center", color: "#8a8a8a", fontSize: 12, fontStyle: "italic", background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12 }}>
            Sin documentos {filterType !== "all" || search.trim() ? "con esos filtros" : ""}
          </div>
        )}
        {!loading && filtered.map(d => {
          const ref = refOf(d);
          const color = DOC_TYPE_COLORS[d.docType] || "#888";
          return (
            <div key={d._id} className="t-02" style={{
              background: "#232323", border: "1px solid #2e2e2e",
              borderRadius: 12, padding: "12px 14px",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 8,
                  background: color + "22", color, border: `1px solid ${color}55`, whiteSpace: "nowrap",
                }}>
                  {DOC_TYPE_LABELS[d.docType] || d.docType}
                </span>
                <span style={{
                  fontSize: 10, color: "#a78bfa", background: "#241a35",
                  border: "1px solid #3d2659", padding: "1px 6px", borderRadius: 8,
                }}>
                  {ref.kind}{ref.count > 1 ? ` ×${ref.count}` : ""}
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: "#8a8a8a" }}>{fmtDate(d.createdAt)}</span>
              </div>
              <div style={{ color: "#fff", fontFamily: "monospace", fontSize: 12, wordBreak: "break-all", marginBottom: d.label ? 2 : 0 }}>
                {d.originalName}
              </div>
              {d.label && (
                <div style={{ fontSize: 11, color: "#b8b8b8" }}>{d.label}</div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, gap: 8 }}>
                <span style={{ fontSize: 11, color: "#8a8a8a" }}>{fmtSize(d.size)}</span>
                <div style={{ display: "flex", gap: 8 }}>
                  <a href={d.url} target="_blank" rel="noopener noreferrer"
                    className="t-02 tap-card"
                    style={{
                      display: "inline-flex", alignItems: "center", minHeight: 38,
                      background: "#3498db22", color: "#3498db",
                      border: "1px solid #3498db55", borderRadius: 10,
                      padding: "6px 16px", fontSize: 12, fontWeight: 600, textDecoration: "none",
                    }}
                  >Abrir</a>
                  <button type="button" onClick={() => descargar(d)}
                    disabled={descargando === d._id}
                    className="t-02 tap-card"
                    style={{
                      display: "inline-flex", alignItems: "center", minHeight: 38,
                      background: "#2ecc7122", color: "#2ecc71",
                      border: "1px solid #2ecc7155", borderRadius: 10,
                      padding: "6px 16px", fontSize: 12, fontWeight: 600,
                      cursor: descargando === d._id ? "wait" : "pointer",
                      fontFamily: "inherit",
                    }}
                  >{descargando === d._id ? "Descargando…" : "Descargar"}</button>
                  <button type="button" onClick={() => eliminar(d)} disabled={mutating === d._id}
                    style={{ background: "#e74c3c22", color: "#e74c3c", border: "1px solid #e74c3c55", borderRadius: 10, padding: "6px 12px", fontSize: 12, cursor: "pointer" }}>
                    {mutating === d._id ? "Eliminando…" : "Eliminar"}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      ) : (
      <div style={{
        border: "1px solid #2e2e2e", borderRadius: 8,
        background: "#242424", overflowX: "auto",
      }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
          <thead>
            <tr style={{ background: "#1a1a1a" }}>
              <Th>Tipo</Th>
              <Th>Archivo</Th>
              <Th>Etiqueta</Th>
              <Th>Asociado a</Th>
              <Th>Tamaño</Th>
              <Th>Subido</Th>
              <Th align="right">Acciones</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#aaa" }}>Cargando…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, textAlign: "center", color: "#aaa", fontStyle: "italic" }}>
                Sin documentos {filterType !== "all" || search.trim() ? "con esos filtros" : ""}
              </td></tr>
            )}
            {filtered.map(d => {
              const ref = refOf(d);
              const color = DOC_TYPE_COLORS[d.docType] || "#888";
              return (
                <tr key={d._id} style={{ borderTop: "1px solid #1f1f1f" }}>
                  <Td>
                    <span style={{
                      fontSize: 10, fontWeight: 700,
                      padding: "2px 7px", borderRadius: 9,
                      background: color + "22", color, border: `1px solid ${color}55`,
                      whiteSpace: "nowrap",
                    }}>
                      {DOC_TYPE_LABELS[d.docType] || d.docType}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ color: "#fff", fontFamily: "monospace", fontSize: 11 }}>
                      {d.originalName}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ color: "#aaa", fontSize: 11 }}>
                      {d.label || <i style={{ color: "#999" }}>—</i>}
                    </span>
                  </Td>
                  <Td>
                    <span style={{
                      fontSize: 10, color: "#a78bfa",
                      background: "#241a35", border: "1px solid #3d2659",
                      padding: "1px 6px", borderRadius: 3,
                    }}>
                      {ref.kind}{ref.count > 1 ? ` ×${ref.count}` : ""}
                    </span>
                  </Td>
                  <Td><span style={{ color: "#cbd5e1", fontSize: 11 }}>{fmtSize(d.size)}</span></Td>
                  <Td><span style={{ color: "#cbd5e1", fontSize: 11 }}>{fmtDate(d.createdAt)}</span></Td>
                  <Td align="right">
                    <a href={d.url} target="_blank" rel="noopener noreferrer"
                      style={{
                        background: "#3498db22", color: "#3498db",
                        border: "1px solid #3498db55", borderRadius: 4,
                        padding: "3px 10px", fontSize: 10, fontWeight: 600,
                        textDecoration: "none", marginRight: 4,
                      }}
                    >Abrir</a>
                    <button type="button" onClick={() => descargar(d)}
                      disabled={descargando === d._id}
                      style={{
                        background: "#2ecc7122", color: "#2ecc71",
                        border: "1px solid #2ecc7155", borderRadius: 4,
                        padding: "3px 10px", fontSize: 10, fontWeight: 600,
                        cursor: descargando === d._id ? "wait" : "pointer",
                        fontFamily: "inherit",
                      }}
                    >{descargando === d._id ? "Descargando…" : "Descargar"}</button>
                    <button type="button" onClick={() => eliminar(d)} disabled={mutating === d._id}
                      style={{ background: "#e74c3c22", color: "#e74c3c", border: "1px solid #e74c3c55", borderRadius: 4, padding: "3px 10px", marginLeft: 4, fontSize: 10, cursor: "pointer" }}>
                      {mutating === d._id ? "…" : "Eliminar"}
                    </button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      <div style={{ marginTop: 8, fontSize: 11, color: "#aaa" }}>
        {filtered.length} documento{filtered.length === 1 ? "" : "s"}
        {filterType !== "all" || search.trim() ? ` (de ${docs.length} totales)` : ""}
      </div>
    </div>
  );
};

const DocCard = ({ label, value, color, onClick, active }) => (
  <div onClick={onClick} style={{
    padding: "8px 12px", minWidth: 100,
    background: active ? `${color}22` : "#242424",
    border: `1px solid ${active ? color : "#2e2e2e"}`,
    borderRadius: 6, cursor: "pointer",
  }}>
    <div style={{ fontSize: 9, color: "#999", textTransform: "uppercase", letterSpacing: ".05em" }}>
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, color, marginTop: 2 }}>{value}</div>
  </div>
);

const Th = ({ children, align = "left" }) => (
  <th style={{
    padding: "8px 12px", textAlign: align, fontSize: 9, fontWeight: 700,
    color: "#aaa", textTransform: "uppercase", letterSpacing: ".06em",
    borderBottom: "1px solid #2e2e2e", whiteSpace: "nowrap",
  }}>{children}</th>
);

const Td = ({ children, align = "left" }) => (
  <td style={{
    padding: "8px 12px", textAlign: align, fontSize: 12,
    color: "#bbb", verticalAlign: "middle",
  }}>{children}</td>
);

export default Documentos;
